// VOY-417 §6 — Recent searches (context view).
//
// Framed as "the agent's raw input signal" — what Voy is reading to
// build your profile. Distinct from Profile's click-to-rerun list (same
// data, different framing).
//
// Read-only here. No re-run handoff, no edit. Just a context list with
// timestamps so the user can see what observations are feeding the
// agent. A small "Re-run on Profile" link deep-links to Profile's
// section for re-running.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Plane, Hotel, Search as SearchIcon } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import type { SavedSearch } from '@/types/profile'

export interface RecentSearchesContextSectionProps {
  userId: string
}

function iconFor(type: SavedSearch['search_type']) {
  if (type === 'flight') return <Plane aria-hidden="true" />
  if (type === 'hotel') return <Hotel aria-hidden="true" />
  return <SearchIcon aria-hidden="true" />
}

function summary(s: SavedSearch): string {
  const dest = s.destination_city_name || (s.query as { destination?: string })?.destination
  const origin = s.origin_city_name || (s.query as { origin?: string })?.origin
  if (origin && dest) return `${origin} → ${dest}`
  return dest || origin || 'Search'
}

function relativeTime(iso: string): string {
  try {
    const ms = Date.now() - new Date(iso).getTime()
    if (ms < 60 * 1000) return 'just now'
    if (ms < 60 * 60 * 1000) return `${Math.floor(ms / (60 * 1000))}m ago`
    if (ms < 24 * 60 * 60 * 1000) return `${Math.floor(ms / (60 * 60 * 1000))}h ago`
    const days = Math.floor(ms / (24 * 60 * 60 * 1000))
    if (days < 30) return `${days}d ago`
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

export function RecentSearchesContextSection({ userId }: RecentSearchesContextSectionProps) {
  const [searches, setSearches] = useState<SavedSearch[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return
    void (async () => {
      setLoading(true)
      try {
        // Direct Supabase read — same pattern as the Profile version
        // (RLS on saved_searches enforces auth.uid() = user_id).
        const { data, error } = await supabase
          .from('saved_searches')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(8)
        if (error) {
          console.error('[RecentSearchesContextSection] load failed:', error)
          setSearches([])
        } else {
          setSearches((data as SavedSearch[]) || [])
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [userId])

  return (
    <section className="card" aria-labelledby="recent-context-h">
      <div className="card-h">
        <h3 id="recent-context-h">Recent searches</h3>
        <span className="meta">{loading ? 'LOADING…' : 'AGENT INPUT'}</span>
      </div>
      <p className="you-context-blurb">
        These are the searches Voy is reading to build your profile.
      </p>

      {!loading && searches.length === 0 ? (
        <div className="you-empty-state" style={{ paddingTop: 8 }}>
          <p className="you-empty-sub">
            No recent searches. As you search, this list grows — and the agent picks up signal from each one.
          </p>
        </div>
      ) : (
        <ul className="you-recent-list">
          {searches.map((s) => (
            <li key={s.id} className="you-recent-row">
              <span className="you-recent-icon">{iconFor(s.search_type)}</span>
              <span className="you-recent-summary">{summary(s)}</span>
              <span className="you-recent-time">{relativeTime(s.created_at)}</span>
            </li>
          ))}
        </ul>
      )}

      {searches.length > 0 && (
        <div className="you-recent-foot">
          <Link to="/profile#searches-h" className="you-inline-link">
            Re-run on Profile <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      )}
    </section>
  )
}
